import { OffsetUnit, WorkflowId } from "@sp-crm/core";
import { Input } from "components/ui/input";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import { produce } from "immer";
import React from "react";
import {
    computeWorkflowDelay,
    hasEditableDelay,
    isTokenDelayRecipe,
} from "./manage-workflows-helper";
import { EditableWorkflow } from "./types";

interface DelayRecipeEditorProps {
    workflow: EditableWorkflow;
    onCommit: (delayRecipe: string) => void;
}

interface State {
    status: "pristine" | "dirty" | "pending" | "ignore";
    workflowId: WorkflowId;
    upstreamDelay: number;
    upstreamUnits: OffsetUnit;
    currentDelay: number;
    currentUnit: OffsetUnit;
}

const initialState = (workflow: EditableWorkflow): State => {
    if (isTokenDelayRecipe(workflow.delayRecipe)) {
        return {
            workflowId: workflow.id,
            status: "ignore",
            upstreamDelay: workflow.delay,
            upstreamUnits: "d",
            currentDelay: workflow.delay,
            currentUnit: "d",
        };
    }

    const [delay, units] = computeWorkflowDelay(workflow);

    return {
        workflowId: workflow.id,
        status: "pristine",
        upstreamDelay: delay,
        upstreamUnits: units,
        currentDelay: delay,
        currentUnit: units,
    };
};

type Action =
    | { type: "changeDelay"; delay: number }
    | { type: "changeUnit"; unit: OffsetUnit }
    | { type: "upstreamChanged"; workflow: EditableWorkflow }
    | { type: "commit"; delay: number; unit: OffsetUnit };

const reducer = (state: State, action: Action): State => {
    return produce(state, draft => {
        if (action.type === "changeDelay") {
            draft.status = "dirty";
            draft.currentDelay = action.delay;
        }

        if (action.type === "changeUnit") {
            draft.status = "dirty";
            draft.currentUnit = action.unit;
        }

        if (action.type === "upstreamChanged") {
            const anchoredToDateField = isTokenDelayRecipe(action.workflow.delayRecipe);

            if (
                action.workflow.id !== draft.workflowId ||
                anchoredToDateField !== (state.status === "ignore")
            ) {
                Object.assign(draft, initialState(action.workflow));
            } else {
                const [delay, units] = computeWorkflowDelay(action.workflow);

                draft.upstreamDelay = delay;
                draft.upstreamUnits = units;

                if (
                    state.status === "pending" &&
                    state.currentDelay === delay &&
                    state.currentUnit === units
                ) {
                    draft.status = "pristine";
                }

                if (state.status === "pristine") {
                    draft.currentDelay = delay;
                    draft.currentUnit = units;
                }
            }
        }

        if (action.type === "commit") {
            draft.status =
                state.upstreamDelay === action.delay &&
                state.upstreamUnits === action.unit
                    ? "pristine"
                    : "pending";
            draft.currentDelay = action.delay;
            draft.currentUnit = action.unit;
        }
    });
};

export interface DelayRecipeEditorControl {
    visible: boolean;
    editable: boolean;
    pending: boolean;
    delay: number;
    unit: OffsetUnit;
    handleDelayChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleUnitsChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    commitDelayChange: () => void;
    commitDelay: (delay: number, unit: OffsetUnit) => void;
}

export const useDelayRecipeEditor = (
    workflow: EditableWorkflow,
    onCommit: (delayRecipe: string) => void,
): DelayRecipeEditorControl => {
    const [state, dispatch] = React.useReducer(reducer, initialState(workflow));

    const editable = !!workflow.regionVisibility?.editable;

    const handleDelayChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            dispatch({ type: "changeDelay", delay: e.target.valueAsNumber });
        },
        [],
    );

    const handleUnitsChange = React.useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const newUnit = e.target.value as OffsetUnit;
            dispatch({ type: "changeUnit", unit: newUnit });
            dispatch({ type: "commit", delay: state.currentDelay, unit: newUnit });
            onCommit(`${state.currentDelay}${newUnit}`);
        },
        [state.currentDelay, onCommit],
    );

    const commitDelayChange = React.useCallback(() => {
        if (state.status !== "dirty") {
            return;
        }

        const delay = isNaN(state.currentDelay) ? 0 : Math.floor(state.currentDelay);
        const unit = state.currentUnit;

        dispatch({ type: "commit", delay, unit });
        onCommit(`${delay}${unit}`);
    }, [state.status, state.currentDelay, state.currentUnit, onCommit]);

    const commitDelay = React.useCallback(
        (delay: number, unit: OffsetUnit) => {
            dispatch({ type: "commit", delay, unit });
            onCommit(`${delay}${unit}`);
        },
        [onCommit],
    );

    React.useEffect(() => {
        dispatch({ type: "upstreamChanged", workflow });
    }, [workflow.id, workflow.delay, workflow.delayRecipe]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        visible: state.status !== "ignore" && hasEditableDelay(workflow),
        editable,
        pending: state.status === "pending",
        delay: state.currentDelay,
        unit: state.currentUnit,
        handleDelayChange,
        handleUnitsChange,
        commitDelayChange,
        commitDelay,
    };
};

export const DelayRecipeEditor: React.FC<DelayRecipeEditorProps> = props => {
    const { workflow, onCommit } = props;
    const {
        visible,
        editable,
        pending,
        delay,
        unit,
        handleDelayChange,
        handleUnitsChange,
        commitDelayChange,
    } = useDelayRecipeEditor(workflow, onCommit);

    if (!visible) {
        return null;
    }

    return (
        <div className="space-y-1">
            <p>Workflow delay</p>
            <div className="flex items-center space-x-2">
                <Input
                    disabled={!editable}
                    textSize="sm"
                    className="flex-1"
                    value={delay}
                    type="number"
                    onChange={handleDelayChange}
                    onBlur={commitDelayChange}
                />
                <Select
                    disabled={!editable}
                    textSize="sm"
                    value={unit}
                    onChange={handleUnitsChange}>
                    <option value="m">Minutes</option>
                    <option value="h">Hours</option>
                    <option value="d">Days</option>
                </Select>
                {pending ? <Spinner /> : <div className="w-4 h-4" />}
            </div>
            <p className="text-sm text-gray-500">
                Set the delay to run this workflow minutes, hours, or days after the
                criteria is met. We recommend setting this to at least 1 minute for all
                workflows.
            </p>
        </div>
    );
};
